import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { Button, Stack } from "@mui/material";

import { selectExportsByProjectId, useGetExportsQuery } from "fond/api";
import { useCanIConstructionPrintExport } from "fond/hooks/useCanIConstructionPrintExport";
import { useEntityUtil } from "fond/hooks/useEntityUtil";
import { getCurrentProject } from "fond/project/redux";
import { Export, ExportAccordionPanel, ExportsByStatus, ExportStatus, Store } from "fond/types";
import { Actions, permissionCheck } from "fond/utils/permissions";
import { Modal } from "fond/widgets";

import ExportAccordion from "../ExportAccordion";
import NewConstructionPrintsModal from "../NewConstructionPrintsModal";
import NewExportModal from "../NewExportModal";

import { StyledExportIcon } from "./ExportModal.styles";

const EMPTY_EXPORTS: Export[] = [];
type OpenModal = "export" | "constructionPrints" | null;

interface ExportModalProps {
  /**
   * Callback function to handle the onClose of the modal
   */
  onClose(): void;
}

const ExportModal: React.FC<ExportModalProps> = ({ onClose }: ExportModalProps) => {
  /**
   * Controls whether the polling should be active.
   */
  const [polling, setPolling] = useState(true);

  const [openModal, setOpenModal] = useState<OpenModal>(null);
  const project = useSelector((state: Store) => getCurrentProject(state.project));
  const { getEntityLabel } = useEntityUtil();
  useGetExportsQuery(project?.ID, {
    skip: !project?.ID,
    pollingInterval: polling ? 1500 : 0,
    refetchOnMountOrArgChange: true,
  });
  const canEditExport = permissionCheck(project?.Permission.Level, Actions.PROJECT_EXPORT_VIEW);
  const exports = useSelector((state: Store): Export[] => {
    if (!project) return EMPTY_EXPORTS;
    return selectExportsByProjectId(state, project.ID) || EMPTY_EXPORTS;
  });
  const { canCreateConstructionPrints } = useCanIConstructionPrintExport();
  const [expanded, setExpanded] = useState<string | false>(ExportAccordionPanel.Complete);
  const exportsByStatus: ExportsByStatus = useMemo(
    () => ({
      InProgress: exports.filter((item: Export) => [ExportStatus.Pending, ExportStatus.InProgress].includes(item.Status.Value)),
      Complete: exports.filter((item: Export) => item.Status.Value === ExportStatus.Complete),
      Error: exports.filter((item: Export) => item.Status.Value === ExportStatus.Error),
    }),
    [exports]
  );
  const subTitle = project ? getEntityLabel(project, "long").toUpperCase() : "";

  const onExpand = (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpanded(isExpanded ? panel : false);
  };

  const handleExportClick = () => {
    if (canEditExport) {
      setOpenModal("export");
    }
  };

  const handleConstructionPrintsClick = () => {
    if (canEditExport) {
      setOpenModal("constructionPrints");
    }
  };

  /*
   * Determine when the polling should be active/inactive.
   */
  useEffect(() => {
    // If all exports are turned completed, expand the export project accordion.
    if (polling && exportsByStatus.InProgress.length === 0) {
      setExpanded(ExportAccordionPanel.Complete);
    }
  }, [exportsByStatus.InProgress.length, polling]);

  useEffect(() => {
    setPolling(Boolean(exportsByStatus.InProgress.length));
  }, [exportsByStatus.InProgress.length]);

  return (
    <Modal
      showFullscreen
      showClose
      open
      onClose={onClose}
      variant="primary"
      header="Export"
      headerIcon={<StyledExportIcon />}
      headerSubtitle={subTitle}
      data-testid="export-modal"
      content={
        <>
          <Stack direction="row" spacing={2}>
            <Button data-testid="create-new-export-button" variant="contained" color="primary" onClick={handleExportClick} disabled={!canEditExport}>
              Create new export
            </Button>
            {canCreateConstructionPrints && (
              <Button
                data-testid="create-new-construction-prints-button"
                variant="contained"
                color="primary"
                onClick={handleConstructionPrintsClick}
                disabled={!canEditExport}
              >
                Create new construction prints
              </Button>
            )}
          </Stack>
          {openModal === "export" && (
            <NewExportModal onClose={() => setOpenModal(null)} onPostExport={() => setExpanded(ExportAccordionPanel.InProgress)} />
          )}
          {openModal === "constructionPrints" && canCreateConstructionPrints && (
            <NewConstructionPrintsModal onClose={() => setOpenModal(null)} onPostExport={() => setExpanded(ExportAccordionPanel.InProgress)} />
          )}
          <ExportAccordion exportsByStatus={exportsByStatus} expanded={expanded} onExpand={onExpand} />
        </>
      }
      actions={
        <Button data-testid="cancel-button" color="primary" onClick={onClose}>
          Close
        </Button>
      }
    />
  );
};

export default ExportModal;
